/**
 * Super Admin → Administration → Platform domains
 * The base domains tenants can be created under (kk.<base domain>). One is the default.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Globe, Plus, Star, Trash, FloppyDisk, Info } from '@phosphor-icons/react';

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export default function PlatformDomains() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState([]);
  const [defaultDomain, setDefaultDomain] = useState('');
  const [counts, setCounts] = useState({});
  const [newDomain, setNewDomain] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/platform/domains');
      setDomains(res.data.base_domains || []);
      setDefaultDomain(res.data.default_base_domain || '');
      setCounts(res.data.tenant_counts || {});
      setDirty(false);
    } catch (error) {
      toast.error('Failed to load platform domains');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    const d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!DOMAIN_RE.test(d)) { toast.error('Enter a valid domain, e.g. lms.yourcompany.com'); return; }
    if (domains.includes(d)) { toast.error('Already in the list'); return; }
    setDomains([...domains, d]);
    if (!defaultDomain) setDefaultDomain(d);
    setNewDomain('');
    setDirty(true);
  };

  const remove = (d) => {
    if (counts[d]) { toast.error(`${counts[d]} tenant(s) use ${d} — move them first`); return; }
    const next = domains.filter((x) => x !== d);
    setDomains(next);
    if (defaultDomain === d) setDefaultDomain(next[0] || '');
    setDirty(true);
  };

  const save = async () => {
    if (!domains.length) { toast.error('At least one base domain is required'); return; }
    setSaving(true);
    try {
      await api.put('/admin/platform/domains', { base_domains: domains, default_base_domain: defaultDomain });
      toast.success('Platform domains saved');
      try { localStorage.removeItem('lms_platform_domains'); } catch { /* ignore */ }
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const rows = domains.map((d) => ({ id: d, domain: d, is_default: d === defaultDomain, tenants: counts[d] || 0 }));

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="platform-domains-page"
        title="Platform domains"
        subtitle="Base domains that tenant portals are created under — e.g. acme.mylms.in"
        badge={<StatusBadge value={`${domains.length} domain${domains.length === 1 ? '' : 's'}`} tone="info" />}
        onBack={null}
        onRefresh={load}
        refreshing={loading}
        dirty={dirty}
        loading={loading && !domains.length}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : 'Save', icon: FloppyDisk, onClick: save, disabled: saving || !dirty, testid: 'save-domains-btn' },
          { key: 'reload', label: 'Reload', variant: 'outline', onClick: load },
        ]}
      >
        <FormSection title="Add a base domain" icon={Globe} bodyClassName="p-4 space-y-3">
          <div className="flex items-end gap-2 max-w-2xl">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="new-domain" className="text-xs font-medium">Domain</Label>
              <Input
                id="new-domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                placeholder="lms.yourcompany.com"
                className="font-mono"
                data-testid="new-domain-input"
              />
            </div>
            <Button type="button" onClick={add} className="h-9" data-testid="add-domain-btn"><Plus className="w-4 h-4" /> Add</Button>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border border-border rounded-md p-3 max-w-3xl">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-shell-primary" />
            <div className="space-y-1">
              <p>For a base domain to work, its DNS must send every subdomain to this platform:</p>
              <p className="font-mono">CNAME &nbsp;*.lms.yourcompany.com &nbsp;→&nbsp; mylms.up.railway.app &nbsp;(or your frontend's custom domain)</p>
              <p>and the wildcard host must be added to the frontend service's domains (Railway → Settings → Networking) so TLS is issued. Also add <span className="font-mono">https://*.lms.yourcompany.com</span> to the backend's <span className="font-mono">CORS_ORIGINS</span>.</p>
            </div>
          </div>
        </FormSection>

        <DataGrid
          title="Base domains"
          rows={rows}
          loading={loading}
          pagination={false}
          selectable={false}
          columns={[
            { key: 'domain', header: 'Domain', className: 'font-mono', render: (r) => <span className="inline-flex items-center gap-2 font-mono">{r.domain}{r.is_default && <StatusBadge value="default" tone="success" />}</span> },
            { key: 'example', header: 'Tenant URL pattern', accessor: (r) => `acme.${r.domain}`, className: 'font-mono text-xs text-muted-foreground' },
            { key: 'tenants', header: 'Tenants', align: 'right' },
          ]}
          rowActions={(r) => (
            <div className="inline-flex gap-1">
              <Button size="sm" variant={r.is_default ? 'secondary' : 'outline'} className="h-8" disabled={r.is_default} onClick={() => { setDefaultDomain(r.domain); setDirty(true); }} data-testid={`default-${r.domain}`}>
                <Star className="w-3.5 h-3.5" weight={r.is_default ? 'fill' : 'regular'} /> {r.is_default ? 'Default' : 'Make default'}
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-destructive" disabled={r.tenants > 0 || rows.length === 1} onClick={() => remove(r.domain)} title={r.tenants ? 'In use by tenants' : 'Remove'} data-testid={`remove-${r.domain}`}>
                <Trash className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          emptyMessage="No base domains configured"
        />
      </RecordPage>
    </DashboardLayout>
  );
}
